<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="defaultColumnOrderIndex"
      :defaultPageLength="100"
      :url="dataURL"
      :filterOptions="orderFilterOptions"
      :displayTools="true"
      :hasDateRangeFilter="true"
      :debounceTime="300"
      :displayExportTools="true"
      exportTitle="Despatchable Orders">
      
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:tableHeaderCell="tableHeaderCell">
        <span v-if="tableHeaderCell.data == addRunsheetColumnIndex && selectionEnabled" class="ml-2">
          <FormButton class="btn-xxs btn-default mr-1" @click="selectAllVisible">Select All</FormButton>
          <FormButton class="btn-xxs btn-default ml-1" @click="unselectAllVisible">Unselect All</FormButton>
        </span>
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" :newTab="openNewTab" />
          </td>
          <td>
            {{row.data.contactName}}
          </td>
          <td>
            {{row.data.customerRef | formatHyphenEmptyValue}}
          </td>
          <td>
            <LabelBadge :labelClass="`label-order-type-${row.data.type}`">{{row.data.type | formatLookupName(OrderTypeLookup)}}</LabelBadge>
          </td>
          <td>
            <LabelOrderStatusBadge
              :status="row.data.status"
              :isOnHold="row.data.isOnHold"
              :holdType="row.data.holdType"
              :isUrgent="row.data.isUrgent"
              :isActiveTasksAssigned="row.data.isActiveTasksAssigned"
              :isInvoicingComplete="row.data.isInvoicingComplete"
              :isInvoiced="row.data.isInvoiced"
            />
          </td>
          <td>
            <LabelBadge :labelClass="`label-credit-type-${row.data.orderPaymentType}`">{{row.data.orderPaymentType | formatLookupName(OrderPaymentTypeLookup)}}</LabelBadge>
            <LabelBadge labelClass="label-payment-status-paid ml-1" v-if="(row.data.orderPaymentType == OrderPaymentTypes.prepaid || row.data.orderPaymentType == OrderPaymentTypes.cod) && row.data.isPaid">Paid</LabelBadge>
            <LabelBadge labelClass="label-payment-status-unallocated ml-1" v-if="(row.data.orderPaymentType == OrderPaymentTypes.prepaid || row.data.orderPaymentType == OrderPaymentTypes.cod) && !row.data.isPaid">Unpaid</LabelBadge>
          </td>
          <td v-if="!hasDespatchTypeFilter">
            <LabelBadge labelClass="label-despatch-badge">{{row.data.despatchTaskTypeName}}</LabelBadge>
          </td>
          <td>
            <LabelTags :labelList="row.data.deliveryTag" class="d-inline-block" />
          </td>
          <td :class="{'text-danger': isOrderOverdue(row.data)}">
            {{row.data.dateCurrentlyDue | formatDate}}
          </td>
          <td>
            <LabelBadge :labelClass="`label-despatch-ready`" class="ml-1" v-if="row.data.despatchPanelStatus == 'allReady'">All Ready</LabelBadge>
            <LabelBadge :labelClass="`label-despatch-mixed`" class="ml-1" v-if="row.data.despatchPanelStatus == 'production'">In Production</LabelBadge>
            <LabelBadge :labelClass="`label-despatch-not-ready`" class="ml-1" v-if="row.data.despatchPanelStatus == 'notReady'">Not Ready</LabelBadge>
            <LabelBadge :labelClass="`label-despatch-stock`" class="ml-1" v-if="row.data.despatchPanelStatus == 'stock'">Stock</LabelBadge>
          </td>
          <td>
            <FormButton class="btn-default btn-xxs ml-1" @click="viewOrderProductionSummary($event, row.data.id)">View</FormButton>
          </td>
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox :value="isOrderSelected(row.data.id)" @change="updateOrderSelection(row.data.id, $event)" />
          </td>
        </tr>
      </template>
    </DataTable>
    <OrderProductionSummaryPopover ref="orderSummaryPopover" />
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'

import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'
import OrderProductionSummaryPopover from '@/modules/orders/components/orderProductionSummaryPopover'

import { OrderTypes, OrderTypeLookup, OrderStatusTypes, OrderStatusTypeLookup, OrderHoldTypes, OrderHoldTypeLookup, OrderPaymentTypeLookup, OrderPaymentTypes } from '@/store/types'

// Task Despatch - Order Data Table Component
// Used specifically in the new despatch task manager 
export default {
  name: 'TaskDespatchOrderDataTable',
  model: {
    event: 'update'
  },
  components: {
    LabelOrderStatusBadge,
    OrderProductionSummaryPopover
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    despatchTaskTypeId: {
      type: Number,
      default: null
    },
    selectionEnabled: {
      type: Boolean,
      default: true
    },
    useQueryData: Boolean,
    filterContactId: Number,
    filterAccountLocationId: Number,
    filterType: String,

    value: Array
  },
  created() {
    this.OrderTypeLookup = OrderTypeLookup
    this.OrderStatusTypes = OrderStatusTypes
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.OrderHoldTypes = OrderHoldTypes
    this.OrderHoldTypeLookup = OrderHoldTypeLookup
    this.OrderPaymentTypeLookup = OrderPaymentTypeLookup
    this.OrderPaymentTypes = OrderPaymentTypes
  },
  data() {
    let columnList = _.compact([
      { title: "Number", data: "sequenceNumber" },
      { title: "Contact", data: "contactName" },
      { title: "Reference No.", data: "customerRef" },
      { title: "Type", data: "type" },
      { title: "Status", data: "status" },
      { title: "Payment Type", data: "orderPaymentType" },
      this.despatchTaskTypeId == null ? { title: "Despatch", data: "despatchTaskTypeName" } : null,
      { title: "Despatch Tag", data: "deliveryTag" },
      { title: "Est. Completion", data: "dateCurrentlyDue", dateFilter: true, type: 'datetime' },
      { title: "Panel Status", data: "despatchPanelStatus" },
      { title: "Production", data: "", sortable: false, export: false },
      this.selectionEnabled ? { title: "Add Runsheet", data: "", sortable: false, export: false } : null,
    ])

    return {
      breadcrumbs: [
        { title: 'Order', link: '/orders'}
      ],
      columnList: columnList,
      selectedOrderMap: {},
      taskTypeList: []
    }
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {},
        viewType: 'active'      // Active order view type. Has expanded details about active orders, including accounts info
      }
      if(this.filterType)
        params.filter.type = this.filterType
      if(this.filterContactId)
        params.filter.contactId = this.filterContactId
      if(this.filterAccountLocationId)
        params.filter.accountLocationId = this.filterAccountLocationId

      if(this.hasDespatchTypeFilter)
        params.filter.despatchTaskTypeId = this.despatchTaskTypeId

      return '/orders?' + $.param(params)
    },
    defaultColumnOrderIndex() {
      let defaultIndex = 7
      if(!this.hasDespatchTypeFilter)
        defaultIndex++
      return defaultIndex
    },
    addRunsheetColumnIndex() {
      let defaultIndex = 10
      if(!this.hasDespatchTypeFilter)
        defaultIndex++
      return defaultIndex
    },
    orderFilterOptions() {
      var filterOptions = [
        {
          field: 'status',
          name: 'Status',
          options: [
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.production), value: OrderStatusTypes.production, class: 'label-order-status-production' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingCODPaid), value: OrderStatusTypes.pendingCODPaid, class: 'label-order-status-pendingCODPaid' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.despatch), value: OrderStatusTypes.despatch, class: 'label-order-status-despatch' }
          ]
        },
        {
          field: 'isUrgent',
          name: 'Urgent',
          columnIndex: 0,
          options: [
            { name: 'Urgent', value: '1', class: 'label-order-urgent' }
          ]
        },
        {
          field: 'isActiveTasksAssigned',
          name: 'Tasks Assigned',
          columnIndex: 0,
          options: [
            { name: 'Tasks Assigned', value: '1', class: 'label-order-tasks-assigned' },
            { name: 'No Tasks Assigned', value: '0', class: 'label-order-tasks-noneassigned' }
          ]
        },
        {
          field: 'isOnHold',
          name: 'Hold',
          columnIndex: 0,
          options: [
            { name: 'Not On Hold', value: 0, class: 'label-order-hold' },
          ]
        },
        {
          field: 'orderPaymentType',
          name: 'Payment Type',
          columnIndex: 1,
          options: [
            { name: Utils.getLookupName(OrderPaymentTypeLookup, OrderPaymentTypes.prepaid), value: OrderPaymentTypes.prepaid, class: 'label-credit-type-prepaid' },
            { name: Utils.getLookupName(OrderPaymentTypeLookup, OrderPaymentTypes.cod), value: OrderPaymentTypes.cod, class: 'label-credit-type-cod' },
            { name: Utils.getLookupName(OrderPaymentTypeLookup, OrderPaymentTypes.account), value: OrderPaymentTypes.account, class: 'label-credit-type-account' },
          ]
        }
      ]
      if(!this.hasDespatchTypeFilter) {
        filterOptions.push({
          field: 'despatchTaskTypeId',
          name: 'Despatch Task Type',
          columnIndex: 1,
          options: this.taskTypeList.map(taskType => {
            return {
              name: taskType.name,
              value: taskType.id,
              class: 'label-despatch-badge'
            }
          })
        })
      }

      let deliveryTagLabels = this.$store.getters['label/getLabelsByType']('contactAddressDelivery')
      filterOptions.push({
        name: 'Despatch Tag',
        field: 'deliveryTagName',   // The extracted 'name' component from the order delivery tag
        columnIndex: 1,
        options: deliveryTagLabels.map(labelInfo => {
          return {
            name: labelInfo.name,
            value: labelInfo.name,
            colour: labelInfo.colour
          }
        })
      })

      filterOptions.push(
        {
          field: 'despatchPanelStatus',
          name: 'Panel Status',
          columnIndex: 2,
          options: [
            { name: 'Not Ready', value: 'notReady', class: 'label-despatch-not-ready' },
            { name: 'In Production', value: 'production', class: 'label-despatch-mixed' },
            { name: 'All Ready', value: 'allReady', class: 'label-despatch-ready' },
            { name: 'Stock', value: 'stock', class: 'label-despatch-stock' },
          ]
        }
      )

      return filterOptions
    },
    hasDespatchTypeFilter() {
      return this.despatchTaskTypeId != null
    }
  },
  watch: {
    value: function() {
      this.updateSelectedOrderMap()
    }
  },  
  mounted() {
    this.requestFilterLabels()
    this.updateSelectedOrderMap()

    // Apply default filters
    this.$nextTick(() => {
      let defaultFilters = _.compact([
        { value: 'despatch', field: 'status' },
        { value: 'production', field: 'status' },
        { value: 'pendingCODPaid', field: 'status' },
        this.hasDespatchTypeFilter ? { value: this.despatchTaskTypeId, field: 'despatchTaskTypeId' } : null,
        { value: 'production', field: 'despatchPanelStatus' },
        { value: 'allReady', field: 'despatchPanelStatus' },
        { value: 'stock', field: 'despatchPanelStatus' },
        { value: '0', field: 'isActiveTasksAssigned' },
        { value: '0', field: 'isOnHold' }
      ])

      this.$refs.dataTable.applySelectedFilters(defaultFilters)
    })
  },
  methods: {
    reload() {
      this.$refs.dataTable.reload()
    },
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {
          // Get labels / Task types for the filter popover
          var results = await Promise.all([
            this.$apiService.coreService.getLabelsForType('contactAddressDelivery'),
            this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 }})
          ])

          let deliveryTagLabels = results[0].data
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'contactAddressDelivery',
            labelList: deliveryTagLabels
          })
          this.taskTypeList = results[1].data

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    },
    isOrderSelected(orderId) {
      return this.selectedOrderMap[orderId]
    },
    updateOrderSelection(orderId, selected) {
      this.selectedOrderMap[orderId] = selected
      this.updateOrderList()
    },
    selectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(order => {
        this.$set(this.selectedOrderMap, order.id, true)
      })
      this.updateOrderList()
    },
    unselectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(order => {
        this.$set(this.selectedOrderMap, order.id, false)
      })
      this.updateOrderList()
    },
    updateSelectedOrderMap() {
      this.selectedOrderMap = {}
      if(this.value) {
        _.uniq(this.value).forEach(orderId => {
          this.selectedOrderMap[orderId] = true
        })
      }
    },
    updateOrderList() {
      this.$emit('update', Object.keys(this.selectedOrderMap).filter(orderId => this.selectedOrderMap[orderId]).map(orderId => parseInt(orderId)))
    },
    isOrderOverdue(order) {
      if(!order.dateCurrentlyDue)
        return false
      return moment(order.dateCurrentlyDue).isBefore(moment())
    },
    viewOrderProductionSummary(ev, orderId) {
      this.$refs.orderSummaryPopover.displayFromElement(ev.target, orderId)
    }
  }
};
</script>
